import React, { useState, useContext, useCallback, useRef, useEffect, Suspense, lazy } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import luxon3Plugin from '@fullcalendar/luxon3';
import { UserContext } from '../contexts/UserContext';
import { apiCallJson, buildAuthHeaders, checkTokenAndLogout } from '../utils/auth';
import { TIMEZONE_OPTIONS, HOME_TIMEZONE } from '../utils/timezone';
import { toCalendarEvent } from './Calendar/utils/calendarHelpers';
import {
    customFrLocale,
    customKoLocale,
    customEnLocale,
    getCalendarLocale
} from './Calendar/utils/localeConfig';
// Calendar.css 스타일 재사용 (GNEGNEGNE 캘린더와 동일)
import './Calendar.css';
import styles from './PersonalCalendar.module.css';

const AddEventModal = lazy(() => import('./AddEventModal'));
const EditEventModal = lazy(() => import('./EditEventModal'));

// 다국어 텍스트
const TEXT = {
    timezoneLabel: {
        ko: '타임존',
        en: 'Timezone',
        fr: 'Fuseau horaire'
    },
    today: { ko: '오늘', en: 'Today', fr: "Auj." },
    month: { ko: '월', en: 'M.', fr: 'M.' },
    week: { ko: '주', en: 'W.', fr: 'S.' },
    day: { ko: '일', en: 'D.', fr: 'J.' },
    expandEarlyHours: { ko: '0-8', en: '0-8', fr: '0-8' },
    collapseEarlyHours: { ko: '0-8', en: '0-8', fr: '0-8' }
};

const MODAL_LOADING_TEXT = {
    ko: '모달 불러오는 중...',
    en: 'Loading modal...',
    fr: 'Chargement...'
};

const PersonalCalendarModalFallback = ({ lang }) => (
    <div
        style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(12, 12, 12, 0.18)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000001,
            pointerEvents: 'none',
        }}
    >
        <div
            style={{
                backgroundColor: 'rgba(255, 255, 255, 0.94)',
                color: '#111',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                borderRadius: '14px',
                padding: '14px 18px',
                boxShadow: '0 12px 30px rgba(0, 0, 0, 0.12)',
                fontSize: '14px',
                fontWeight: 600,
            }}
        >
            {MODAL_LOADING_TEXT[lang] || MODAL_LOADING_TEXT.ko}
        </div>
    </div>
);

// 최근 장소 저장용 LocalStorage 키
const RECENT_LOCATIONS_KEY = 'personal_calendar_recent_locations';
const LAST_TIMEZONE_KEY = 'personal_calendar_last_timezone';

// 최근 장소 가져오기
export function getRecentLocations() {
    try {
        const saved = localStorage.getItem(RECENT_LOCATIONS_KEY);
        return saved ? JSON.parse(saved) : [];
    } catch {
        return [];
    }
}

// 장소 저장하기
export function saveRecentLocation(location) {
    if (!location || location.trim() === '') return;
    try {
        const locations = getRecentLocations();
        const filtered = locations.filter(l => l !== location);
        const updated = [location, ...filtered].slice(0, 10);
        localStorage.setItem(RECENT_LOCATIONS_KEY, JSON.stringify(updated));
    } catch {
        // ignore
    }
}

// 마지막 사용 타임존 가져오기
export function getLastTimezone() {
    try {
        return localStorage.getItem(LAST_TIMEZONE_KEY) || null;
    } catch {
        return null;
    }
}

// 타임존 저장하기
export function saveLastTimezone(timezone) {
    if (!timezone) return;
    try {
        localStorage.setItem(LAST_TIMEZONE_KEY, timezone);
    } catch {
        // ignore
    }
}

/**
 * PersonalCalendar - 개인 게스트 스팟 관리용 캘린더
 * GNEGNEGNE 캘린더와 동일한 스타일 사용
 */
export default function PersonalCalendar() {
    const { userInfo } = useContext(UserContext);
    const lang = userInfo?.preferred_language || 'fr';
    const calendarRef = useRef(null);

    // 표시용 타임존 (기본: Paris, 로컬 스토리지에서 마지막 선택값 불러오기)
    const [displayTimezone, setDisplayTimezone] = useState(() => {
        const lastTz = getLastTimezone();
        return lastTz || HOME_TIMEZONE;
    });

    // 뷰 상태
    const [currentViewType, setCurrentViewType] = useState('timeGridWeek');
    const [calendarTitle, setCalendarTitle] = useState('');
    const [isEarlyHoursCollapsed, setIsEarlyHoursCollapsed] = useState(true);

    // 모달 상태
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedStartTime, setSelectedStartTime] = useState(null);
    const [selectedEndTime, setSelectedEndTime] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);

    // 노치 배경색을 페이지 배경색과 일치시킴 (PWA)
    useEffect(() => {
        const backgroundColor = '#fffdf8'; // 개인 캘린더 페이지 배경색

        // theme-color 메타 태그 업데이트
        const themeColorMeta = document.querySelector('meta[name="theme-color"]');
        if (themeColorMeta) {
            themeColorMeta.setAttribute('content', backgroundColor);
        }

        // body 배경색도 테마에 맞게 변경 (PWA 노치 여백 포함)
        const originalBodyBg = document.body.style.backgroundColor;
        document.body.style.backgroundColor = backgroundColor;

        return () => {
            // 정리 함수: 페이지를 벗어날 때 기본값으로 복원
            if (themeColorMeta) {
                themeColorMeta.setAttribute('content', '#fffdf8');
            }
            document.body.style.backgroundColor = originalBodyBg;
        };
    }, []);

    // 타임존 변경 핸들러 (로컬 스토리지에 저장)
    const handleTimezoneChange = useCallback((e) => {
        const newTz = e.target.value;
        setDisplayTimezone(newTz);
        saveLastTimezone(newTz);
    }, []);

    // 캘린더 네비게이션 핸들러
    const handlePrev = useCallback(() => {
        const api = calendarRef.current?.getApi();
        if (api) {
            api.removeAllEvents(); // 이전 이벤트 제거 (잔존 방지)
            api.prev();
            setCalendarTitle(api.view.title);
        }
    }, []);

    const handleNext = useCallback(() => {
        const api = calendarRef.current?.getApi();
        if (api) {
            api.removeAllEvents(); // 이전 이벤트 제거 (잔존 방지)
            api.next();
            setCalendarTitle(api.view.title);
        }
    }, []);

    const handleToday = useCallback(() => {
        const api = calendarRef.current?.getApi();
        if (api) {
            api.removeAllEvents(); // 이전 이벤트 제거 (잔존 방지)
            api.today();
            setCalendarTitle(api.view.title);
        }
    }, []);

    const handleChangeView = useCallback((viewType) => {
        const api = calendarRef.current?.getApi();
        if (api) {
            api.changeView(viewType);
            setCurrentViewType(viewType);
            setCalendarTitle(api.view.title);
        }
    }, []);

    // 0-8시 토글
    const toggleEarlyHours = useCallback(() => {
        const api = calendarRef.current?.getApi();
        if (api) {
            const newCollapsed = !isEarlyHoursCollapsed;
            setIsEarlyHoursCollapsed(newCollapsed);
            api.setOption('slotMinTime', newCollapsed ? '08:00:00' : '00:00:00');
        }
    }, [isEarlyHoursCollapsed]);

    // 이벤트 fetch 함수 (API 연동)
    const fetchPersonalEvents = useCallback((fetchInfo, successCallback, failureCallback) => {
        // 로그인 확인 (checkTokenAndLogout returns true if token is invalid/expired)
        if (!userInfo || checkTokenAndLogout()) {
            successCallback([]);
            return;
        }

        const startISO = new Date(fetchInfo.start).toISOString();
        const endISO = new Date(fetchInfo.end).toISOString();

        apiCallJson(`/api/events?start_time=${startISO}&end_time=${endISO}&calendar_type=personal`)
            .then(data => {
                if (!data || !data.events) {
                    successCallback([]);
                    return;
                }

                const mappedEvents = data.events
                    .map(event => {
                        const calEvent = toCalendarEvent(event);
                        if (calEvent) {
                            calEvent.extendedProps = {
                                ...calEvent.extendedProps,
                                calendarType: 'personal',
                                eventTimezone: event.event_timezone,
                                locationName: event.location_name
                            };
                        }
                        return calEvent;
                    })
                    .filter(e => e !== null);

                successCallback(mappedEvents);
            })
            .catch(err => {
                failureCallback(err);
            });
    }, [userInfo]);

    // 홈스튜디오 이벤트 fetch 함수 (배경 이벤트로 표시)
    const fetchHomeStudioEvents = useCallback((fetchInfo, successCallback, failureCallback) => {
        if (!userInfo || checkTokenAndLogout()) {
            successCallback([]);
            return;
        }

        const startISO = new Date(fetchInfo.start).toISOString();
        const endISO = new Date(fetchInfo.end).toISOString();

        // 홈스튜디오 캘린더에서 본인의 이벤트만 가져오기 (calendar_type 없이 호출)
        apiCallJson(`/api/events?start_time=${startISO}&end_time=${endISO}`)
            .then(data => {
                if (!data || !data.events) {
                    successCallback([]);
                    return;
                }

                // 본인의 이벤트만 필터링 (created_by 기준) + bidon 이벤트 제외
                const myEvents = data.events.filter(event => {
                    // bidon 이벤트는 제외
                    if (event.calendar_type === 'bidon') return false;
                    // created_by가 현재 사용자의 user_id와 일치하는 경우
                    return event.created_by === userInfo.user_id;
                });


                const mappedEvents = myEvents
                    .map(event => {
                        const calEvent = toCalendarEvent(event);
                        if (calEvent) {
                            // 배경 이벤트로 설정 (회색, 읽기 전용)
                            calEvent.display = 'background';
                            calEvent.backgroundColor = 'rgba(150, 150, 150, 0.3)';
                            calEvent.borderColor = 'transparent';
                            calEvent.editable = false;
                            calEvent.extendedProps = {
                                ...calEvent.extendedProps,
                                calendarType: 'homeStudio',
                                isBackground: true
                            };
                        }
                        return calEvent;
                    })
                    .filter(e => e !== null);

                successCallback(mappedEvents);
            })
            .catch(() => {
                // 실패해도 빈 배열 반환 (개인 캘린더는 정상 작동)
                successCallback([]);
            });
    }, [userInfo]);

    // 날짜 클릭 핸들러
    const handleDateClick = useCallback((info) => {
        const dateStr = info.dateStr;

        // ISO 문자열에서 날짜와 시간 추출
        if (dateStr.includes('T')) {
            // timeGrid 뷰에서 클릭 (시간 포함)
            const datePart = dateStr.split('T')[0];
            const timePart = dateStr.split('T')[1].substring(0, 5); // HH:MM

            // 30분 후 종료 시간 계산
            const [hours, minutes] = timePart.split(':').map(Number);
            const endMinutes = minutes + 30;
            const endHours = hours + Math.floor(endMinutes / 60);
            const endTimePart = `${String(endHours).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`;

            setSelectedDate(datePart);
            setSelectedStartTime(timePart);
            setSelectedEndDate(datePart);
            setSelectedEndTime(endTimePart);
        } else {
            // dayGrid 뷰에서 클릭 (날짜만) - 하루종일 이벤트
            setSelectedDate(dateStr);
            setSelectedStartTime(null);
            setSelectedEndDate(dateStr); // endDate도 startDate와 동일하게 설정
            setSelectedEndTime(null);
        }
        setShowAddModal(true);
    }, []);

    // 시간 범위 선택 핸들러
    const handleSelect = useCallback((info) => {
        // ISO 문자열에서 날짜와 시간 분리
        const startStr = info.startStr;
        const endStr = info.endStr;

        // 시작 날짜와 시간 추출
        const startDate = startStr.split('T')[0];
        const startTimePart = startStr.includes('T')
            ? startStr.split('T')[1].substring(0, 5)
            : '09:00';

        // 종료 날짜와 시간 추출
        const endDate = endStr.split('T')[0];
        const endTimePart = endStr.includes('T')
            ? endStr.split('T')[1].substring(0, 5)
            : '10:00';

        setSelectedDate(startDate);
        setSelectedStartTime(startTimePart);
        setSelectedEndDate(endDate);
        setSelectedEndTime(endTimePart);
        setShowAddModal(true);
    }, []);

    // 이벤트 클릭 핸들러
    const handleEventClick = useCallback((info) => {
        // 배경 이벤트(홈스튜디오)는 클릭 무시
        if (info.event.extendedProps?.isBackground || info.event.display === 'background') {
            return;
        }

        // FullCalendar EventImpl을 plain object로 변환
        const fcEvent = info.event;
        const plainEvent = {
            id: fcEvent.id,
            title: fcEvent.title,
            start: fcEvent.start?.toISOString() || fcEvent.startStr,
            end: fcEvent.end?.toISOString() || fcEvent.endStr,
            allDay: fcEvent.allDay,
            is_all_day: fcEvent.allDay,
            extendedProps: fcEvent.extendedProps || {}
        };

        setSelectedEvent(plainEvent);
        setShowEditModal(true);
    }, []);

    // 이벤트 추가 완료 핸들러
    const handleEventAdded = useCallback(() => {
        setShowAddModal(false);
        if (calendarRef.current) {
            calendarRef.current.getApi().refetchEvents();
        }
    }, []);

    // 이벤트 수정/삭제 완료 핸들러
    const handleEventUpdated = useCallback(() => {
        setShowEditModal(false);
        setSelectedEvent(null);
        if (calendarRef.current) {
            calendarRef.current.getApi().refetchEvents();
        }
    }, []);

    // 이벤트 드래그 이동 핸들러
    const handleEventDrop = useCallback(async (info) => {
        const event = info.event;

        try {
            const requestBody = {
                title: event.title,
                start_time: event.start?.toISOString(),
                end_time: event.end?.toISOString(),
                is_all_day: event.allDay,
            };

            const res = await fetch(`/api/events/${event.id}`, {
                method: 'PUT',
                headers: buildAuthHeaders({
                    'Content-Type': 'application/json',
                }),
                credentials: 'include',
                body: JSON.stringify(requestBody),
            });

            if (!res.ok) {
                throw new Error('Event update failed');
            }
        } catch (err) {
            info.revert(); // 실패 시 원위치로 복원
            alert('이벤트 이동에 실패했습니다.');
        }
    }, []);

    // 이벤트 리사이즈 핸들러
    const handleEventResize = useCallback(async (info) => {
        const event = info.event;

        try {
            const requestBody = {
                title: event.title,
                start_time: event.start?.toISOString(),
                end_time: event.end?.toISOString(),
                is_all_day: event.allDay,
            };

            const res = await fetch(`/api/events/${event.id}`, {
                method: 'PUT',
                headers: buildAuthHeaders({
                    'Content-Type': 'application/json',
                }),
                credentials: 'include',
                body: JSON.stringify(requestBody),
            });

            if (!res.ok) {
                throw new Error('Event update failed');
            }
        } catch (err) {
            info.revert(); // 실패 시 원위치로 복원
            alert('이벤트 크기 조절에 실패했습니다.');
        }
    }, []);

    // 뷰 변경 시 타이틀 업데이트 및 이벤트 새로고침
    const handleDatesSet = useCallback((arg) => {
        setCalendarTitle(arg.view.title);
        setCurrentViewType(arg.view.type);

        // 날짜 범위 변경 시 이전 이벤트 제거 후 새로 페치 (잔존 현상 방지)
        if (calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            calendarApi.removeAllEvents();
            calendarApi.refetchEvents();
        }
    }, []);

    // 이벤트 커스텀 렌더러 (홈스튜디오 캘린더와 동일)
    const renderEventContent = useCallback((eventInfo) => {
        // 월간 뷰에서는 동그라미 표시
        if (eventInfo.view.type === 'dayGridMonth') {
            const isAllDay = eventInfo.event.allDay;
            const backgroundColor = eventInfo.event.backgroundColor || '#1976d2';
            const borderColor = eventInfo.event.borderColor || backgroundColor;

            return (
                <div className={`event-circle ${isAllDay ? 'all-day' : 'time-based'}`}>
                    <div
                        className="circle"
                        style={{
                            backgroundColor: isAllDay ? backgroundColor : 'transparent',
                            borderColor: borderColor,
                            borderWidth: isAllDay ? '0' : '1px',
                            borderStyle: isAllDay ? 'none' : 'solid',
                            boxSizing: 'border-box',
                            border: isAllDay ? 'none' : `1px solid ${borderColor}`,
                            position: 'relative'
                        }}
                    />
                </div>
            );
        }

        // 주간/일간 뷰에서는 텍스트 표시
        return (
            <div style={{ position: 'relative' }}>
                {eventInfo.event.title}
            </div>
        );
    }, []);

    // 30분 지점 점선 추가를 위한 useEffect
    useEffect(() => {
        const addDashedLines = () => {
            setTimeout(() => {
                const calendarElement = document.querySelector('.calendar-wrapper .fc');
                if (!calendarElement) return;

                const existingDashedLines = calendarElement.querySelectorAll('.dashed-time-line');
                existingDashedLines.forEach(line => line.remove());

                const timeSlots = calendarElement.querySelectorAll('.fc-timegrid-slot');

                timeSlots.forEach(slot => {
                    const existingDashedLine = slot.querySelector('.dashed-time-line');
                    if (existingDashedLine) {
                        existingDashedLine.remove();
                    }

                    const dashedLine = document.createElement('div');
                    dashedLine.className = 'dashed-time-line';

                    slot.style.position = 'relative';
                    slot.appendChild(dashedLine);
                });
            }, 300);
        };

        addDashedLines();

        const handleResize = () => {
            addDashedLines();
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [currentViewType, isEarlyHoursCollapsed]);

    return (
        <div className={styles.container}>
            {/* 타임존 선택 헤더 */}
            <div className={styles.header}>
                <label className={styles.timezoneLabel}>
                    {TEXT.timezoneLabel[lang] || TEXT.timezoneLabel.en}:
                    <select
                        className={styles.timezoneSelect}
                        value={displayTimezone}
                        onChange={handleTimezoneChange}
                    >
                        {TIMEZONE_OPTIONS.map(tz => (
                            <option key={tz.value} value={tz.value}>
                                {tz.label}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            {/* 캘린더 - GNEGNEGNE 스타일 적용 */}
            <div className="calendar-wrapper personal-calendar-wrapper">
                {/* 커스텀 캘린더 헤더 */}
                <div className="calendar-custom-header">
                    <div className="calendar-title-row">
                        <button
                            onClick={handlePrev}
                            style={{
                                width: '40px',
                                height: '40px',
                                border: '1px solid #000',
                                backgroundColor: '#fffdf8',
                                cursor: 'pointer',
                                borderRadius: '50%',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '0',
                                outline: 'none'
                            }}
                        >
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M8 2L4 6L8 10" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                        <span className="calendar-title-text" style={{ fontWeight: 300 }}>{calendarTitle}</span>
                        <button
                            onClick={handleNext}
                            style={{
                                width: '40px',
                                height: '40px',
                                border: '1px solid #000',
                                backgroundColor: '#fffdf8',
                                cursor: 'pointer',
                                borderRadius: '50%',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '0',
                                outline: 'none'
                            }}
                        >
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M4 2L8 6L4 10" stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>
                    {/* 탭 네비게이션 */}
                    <div style={{
                        display: 'flex',
                        marginBottom: '5px',
                        gap: '0',
                        justifyContent: 'center',
                        position: 'relative'
                    }}>
                        <div
                            className="calendar-tab-container"
                            style={{
                                display: 'flex',
                                gap: '0',
                                justifyContent: 'center',
                                border: '1px solid #000',
                                borderRadius: '8px',
                                padding: '0'
                            }}
                        >
                            <button
                                onClick={handleToday}
                                style={{
                                    padding: '4px 16px',
                                    border: '1px solid transparent',
                                    backgroundColor: '#fffdf8',
                                    color: '#000',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '400',
                                    borderTopLeftRadius: '8px',
                                    borderBottomLeftRadius: '8px',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                                }}
                            >
                                {TEXT.today[lang]}
                            </button>
                            <button
                                onClick={() => handleChangeView('dayGridMonth')}
                                style={{
                                    padding: '4px 16px',
                                    border: currentViewType === 'dayGridMonth' ? '1px solid #000' : '1px solid transparent',
                                    backgroundColor: '#fffdf8',
                                    color: '#000',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '400',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    transform: currentViewType === 'dayGridMonth' ? 'scale(1.15)' : 'scale(1)',
                                    zIndex: currentViewType === 'dayGridMonth' ? '2' : '1'
                                }}
                            >
                                {TEXT.month[lang]}
                            </button>
                            <button
                                onClick={() => handleChangeView('timeGridWeek')}
                                style={{
                                    padding: '4px 16px',
                                    border: currentViewType === 'timeGridWeek' ? '1px solid #000' : '1px solid transparent',
                                    backgroundColor: '#fffdf8',
                                    color: '#000',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '400',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    transform: currentViewType === 'timeGridWeek' ? 'scale(1.15)' : 'scale(1)',
                                    zIndex: currentViewType === 'timeGridWeek' ? '2' : '1'
                                }}
                            >
                                {TEXT.week[lang]}
                            </button>
                            <button
                                onClick={() => handleChangeView('timeGridDay')}
                                style={{
                                    padding: '4px 16px',
                                    border: currentViewType === 'timeGridDay' ? '1px solid #000' : '1px solid transparent',
                                    backgroundColor: '#fffdf8',
                                    color: '#000',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '400',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    transform: currentViewType === 'timeGridDay' ? 'scale(1.15)' : 'scale(1)',
                                    zIndex: currentViewType === 'timeGridDay' ? '2' : '1'
                                }}
                            >
                                {TEXT.day[lang]}
                            </button>
                            {/* 0-8시 시간대 토글 버튼 */}
                            <button
                                onClick={currentViewType === 'dayGridMonth' ? undefined : toggleEarlyHours}
                                style={{
                                    padding: '4px 16px',
                                    border: '1px solid transparent',
                                    backgroundColor: '#fffdf8',
                                    color: currentViewType === 'dayGridMonth' ? '#ccc' : '#000',
                                    cursor: currentViewType === 'dayGridMonth' ? 'not-allowed' : 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '400',
                                    borderTopRightRadius: '8px',
                                    borderBottomRightRadius: '8px',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    opacity: currentViewType === 'dayGridMonth' ? '0.6' : '1'
                                }}
                                title={currentViewType === 'dayGridMonth' ? '월간 뷰에서는 사용할 수 없습니다' : (isEarlyHoursCollapsed ? TEXT.expandEarlyHours[lang] : TEXT.collapseEarlyHours[lang])}
                            >
                                <span>0-8h</span>
                                <span style={{ fontSize: '12px' }}>
                                    {isEarlyHoursCollapsed ? '▼' : '▲'}
                                </span>
                            </button>
                        </div>
                    </div>
                </div>

                <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, luxon3Plugin]}
                    timeZone={displayTimezone}
                    initialView="timeGridWeek"
                    headerToolbar={false}
                    locales={[customFrLocale, customKoLocale, customEnLocale]}
                    locale={getCalendarLocale(lang)}
                    eventSources={[
                        {
                            events: fetchHomeStudioEvents,
                            id: 'homeStudioEvents'
                        },
                        {
                            events: fetchPersonalEvents,
                            id: 'personalEvents'
                        }
                    ]}
                    lazyFetching={false}
                    refetchResourcesOnNavigate={true}
                    height="auto"
                    slotMinTime="08:00:00"
                    slotMaxTime="23:00:00"
                    slotDuration="01:00:00"
                    slotLabelInterval="01:00:00"
                    slotLabelFormat={{
                        hour: '2-digit',
                        hour12: false
                    }}
                    snapDuration="00:30:00"
                    allDaySlot={true}
                    editable={true}
                    selectable={true}
                    selectMirror={true}
                    selectLongPressDelay={400}
                    eventLongPressDelay={400}
                    dateClick={handleDateClick}
                    select={handleSelect}
                    eventClick={handleEventClick}
                    eventDrop={handleEventDrop}
                    eventResize={handleEventResize}
                    datesSet={handleDatesSet}
                    eventContent={renderEventContent}
                />
            </div>

            {/* 일정 추가 모달 */}
            {showAddModal && (
                <Suspense fallback={<PersonalCalendarModalFallback lang={lang} />}>
                    <AddEventModal
                        date={selectedDate}
                        startTime={selectedStartTime}
                        endDate={selectedEndDate}
                        endTime={selectedEndTime}
                        onClose={() => setShowAddModal(false)}
                        onEventAdded={handleEventAdded}
                        calendarType="personal"
                        defaultTimezone={displayTimezone}
                    />
                </Suspense>
            )}

            {/* 일정 수정 모달 */}
            {showEditModal && selectedEvent && (
                <Suspense fallback={<PersonalCalendarModalFallback lang={lang} />}>
                    <EditEventModal
                        isOpen={showEditModal}
                        event={selectedEvent}
                        onClose={() => {
                            setShowEditModal(false);
                            setSelectedEvent(null);
                        }}
                        onEventUpdated={handleEventUpdated}
                        onEventDeleted={handleEventUpdated}
                        calendarType="personal"
                    />
                </Suspense>
            )}
        </div>
    );
}
